import { Button, Card, Grid, Group, Table, Tabs, TextInput } from '@mantine/core';
import {
  IconChartBar,
  IconCreditCard,
  IconDeviceFloppy,
  IconEdit,
  IconPlugConnected,
  IconPlus,
  IconReceipt,
  IconSettings,
  IconUsers,
  IconX,
} from '@tabler/icons-react';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import AccountPaymentMethod from '../../components/AccountPaymentMethod';
import AccountSubscriptions from '../../components/AccountSubscriptions';
import AccountUsage from '../../components/AccountUsage';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import CollaboratorModal from '../../components/Modals/CollaboratorModal';
import AccountBillingFields from '../../components/Onboarding/AccountBillingFields';
import AccountFlatPriceSection, { flatPriceError } from '../../components/Settings/AccountFlatPriceSection';
import SettingsTabConnectedApps from '../../components/Settings/Tabs/SettingsTabConnectedApps';
import Spinner from '../../components/Spinner';
import { Page, SettingsAccountTabs } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';
import { setAccount as setAccountRedux } from '../../store/accountSlice';

const SettingsAccountPage = () => {
  const dispatch = useDispatch();
  const fetch = useFetch();
  const location = useLocation();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);
  const { account: accountFromRedux } = useSelector((state) => state.account);
  const { domain } = useSelector((state) => state.domain);
  const isShopify = domain.platform === 'shopify';
  const tabs = ['account', 'usage', 'collaborators', 'paymentMethod', 'subscriptions', 'createShop'].filter(
    (t) => t !== 'paymentMethod' || domain.platform !== 'shopify'
  );
  const tab = queryString.parse(location.search).tab || tabs[0];
  const collaboratorModalOpen = queryString.parse(location.search).collaboratorModalOpen;
  const [error, setError] = useState();
  const [userRole, setUserRole] = useState();
  const [account, setAccount] = useState(accountFromRedux);
  const [userRoles, setUserRoles] = useState();

  const isDirty =
    JSON.stringify({
      ...account,
      paymentMethod: undefined,
      userRoles: undefined,
      domains: undefined,
      partner: undefined,
      updatedAt: undefined,
    }) !==
    JSON.stringify({
      ...accountFromRedux,
      paymentMethod: undefined,
      userRoles: undefined,
      domains: undefined,
      partner: undefined,
      updatedAt: undefined,
    });

  useEffect(() => {
    setAccount(accountFromRedux);
  }, [accountFromRedux]);

  useEffect(() => {
    if (userRoles !== undefined) {
      return;
    }
    fetch(`/api/admin/accounts/${account.id}/userRoles`)
      .then(({ userRoles }) => {
        setUserRoles(userRoles);
      })
      .catch(setError);
  }, [account.id, fetch, userRoles]);

  const saveUserAndClose = (userRole) => {
    setError(undefined);
    if (userRole.id) {
      fetch(`/api/admin/accounts/${account.id}/userRoles/${userRole.id}`, {
        method: 'PUT',
        body: JSON.stringify(userRole),
      })
        .then(({ userRole }) => {
          setUserRoles(undefined);
          setUserRole(undefined);
        })
        .catch(setError);
    } else {
      fetch(`/api/admin/accounts/${account.id}/userRoles`, {
        method: 'POST',
        body: JSON.stringify(userRole),
      })
        .then(({ userRole }) => {
          setUserRoles(undefined);
          setUserRole(undefined);
        })
        .catch(setError);
    }
  };

  const deleteRole = (userRole) => {
    setError(undefined);
    fetch(`/api/admin/accounts/${account.id}/userRoles/${userRole.id}`, {
      method: 'DELETE',
    })
      .then(() => {
        setUserRoles(undefined);
      })
      .catch(setError);
  };

  const setTab = (tab) => {
    if (tab === 'createShop') {
      navigate(`/accounts/${account.id}/domains/new`);
    } else {
      navigate(location.pathname.replace(`/settings/account`, `/settings/account?tab=${tab}`));
    }
  };

  const addCollaborator = () => {
    setUserRole({ role: 'collaborator', user: {} });
  };

  useEffect(() => {
    if (collaboratorModalOpen === '1') {
      addCollaborator();
    } else {
      setUserRole(undefined);
    }
  }, [collaboratorModalOpen]);

  const submitForm = (e) => {
    e.preventDefault();
    setError(undefined);
    fetch(`/api/admin/accounts/${account.id}`, {
      method: 'PUT',
      body: JSON.stringify(account),
    })
      .then(({ account }) => {
        dispatch(setAccountRedux(account));
      })
      .catch(setError);
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (!user || userRoles === undefined) {
    return <Spinner />;
  }

  const users = userRoles.map((r) => {
    if (!r.user) {
      return null;
    }

    let buttons = '';
    if (user.id !== r.userId) {
      buttons = (
        <Group gap="xs">
          <Button
            variant="default"
            size="xs"
            type="button"
            onClick={() => setUserRole(r)}
            style={{ padding: '0pt 5pt 0pt 5pt', marginRight: '5pt' }}
          >
            <IconEdit size={12} />
          </Button>
          <Button
            color="red"
            size="xs"
            type="button"
            style={{ padding: '0pt 5pt 0pt 5pt' }}
            onClick={() => deleteRole(r)}
          >
            <IconX size={12} />
          </Button>
        </Group>
      );
    }
    return (
      <Table.Tr key={r.id}>
        <Table.Td visibleFrom="md">{r.user.firstName}</Table.Td>
        <Table.Td visibleFrom="md">{r.user.lastName}</Table.Td>
        <Table.Td>{r.user.email}</Table.Td>
        <Table.Td>{r.user.phoneNumber}</Table.Td>
        <Table.Td>{r.role}</Table.Td>
        <Table.Td>{buttons}</Table.Td>
      </Table.Tr>
    );
  });

  return (
    <div>
      <Metadata title={`${Page['settings']} | ${SettingsAccountTabs[tab]}`} />
      <CollaboratorModal
        close={() => setUserRole(undefined)}
        saveAndClose={(userRole) => saveUserAndClose(userRole)}
        userRole={userRole}
        setUserRole={setUserRole}
      />
      <Grid pt={30} my={20}>
        <Grid.Col span={12}>
          <Tabs value={tab} onChange={setTab} keepMounted>
            <Tabs.List mb="md">
              <Tabs.Tab value="account" leftSection={<IconSettings size={16} />}>
                Account
              </Tabs.Tab>
              <Tabs.Tab value="usage" leftSection={<IconChartBar size={16} />}>
                Usage
              </Tabs.Tab>
              <Tabs.Tab
                value="collaborators"
                leftSection={<IconUsers size={16} />}
                data-tour="accountSettingsCollaboratorsTab"
              >
                Collaborators
              </Tabs.Tab>
              {!isShopify && (
                <Tabs.Tab value="paymentMethod" leftSection={<IconCreditCard size={16} />}>
                  Payment method
                </Tabs.Tab>
              )}
              <Tabs.Tab value="subscriptions" leftSection={<IconReceipt size={16} />}>
                Subscriptions
              </Tabs.Tab>
              <Tabs.Tab value="connectedApps" leftSection={<IconPlugConnected size={16} />}>
                Connected apps
              </Tabs.Tab>
              {!isShopify && (
                <Tabs.Tab
                  value="createShop"
                  leftSection={<IconPlus size={16} />}
                  style={{ position: 'absolute', right: 0 }}
                >
                  New Shop
                </Tabs.Tab>
              )}
            </Tabs.List>
            <Tabs.Panel value="account">
              <Card withBorder>
                <Grid>
                  <Grid.Col span={3}></Grid.Col>
                  <Grid.Col span={6}>
                    <form onSubmit={(e) => submitForm(e)}>
                      <TextInput
                        label="Account Name"
                        name="name"
                        type="text"
                        placeholder="My Awesome Shop"
                        value={account.name}
                        error={account.name !== undefined && account.name === '' ? 'name is required' : null}
                        onChange={(e) => setAccount({ ...account, name: e.target.value })}
                        disabled={account.partner}
                        mb="md"
                      />

                      <TextInput
                        label="Contact Email*"
                        name="contactEmail"
                        type="text"
                        required={true}
                        error={
                          (account.contactEmail !== undefined && account.contactEmail === '') ||
                          (error && error.errors?.find((e) => e.path === 'contactEmail'))
                            ? error && error.errors?.find((e) => e.path === 'contactEmail')
                              ? error.errors?.find((e) => e.path === 'contactEmail').message
                              : 'contact email is required'
                            : null
                        }
                        value={account.contactEmail || ''}
                        onChange={(e) => setAccount({ ...account, [e.target.name]: e.target.value })}
                        mb="md"
                      />

                      <AccountBillingFields billingInfo={account} setBillingInfo={setAccount} error={error} />
                      <AccountFlatPriceSection account={account} setAccount={setAccount} isShopify={isShopify} />
                      <Group justify="flex-end">
                        <Button
                          disabled={
                            !account.name ||
                            !account.addressLine1 ||
                            !account.city ||
                            !account.country ||
                            !account.contactEmail ||
                            !account.contactName ||
                            !account.contactPhoneNumber ||
                            flatPriceError(account) !== null ||
                            !isDirty
                          }
                          type="submit"
                        >
                          <IconDeviceFloppy size={14} /> {isDirty ? 'Save' : 'Saved'}
                        </Button>
                      </Group>
                    </form>
                  </Grid.Col>
                </Grid>
              </Card>
            </Tabs.Panel>
            <Tabs.Panel value="usage">
              <Card withBorder>
                <AccountUsage />
              </Card>
            </Tabs.Panel>
            <Tabs.Panel value="collaborators">
              <Card withBorder>
                <Table striped withTableBorder highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th visibleFrom="md">First Name</Table.Th>
                      <Table.Th visibleFrom="md">Last Name</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Phone Number</Table.Th>
                      <Table.Th>Role</Table.Th>
                      <Table.Th></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>{users}</Table.Tbody>
                </Table>
                <Button
                  data-tour="accountSettingsCollaboratorsTabAddButton"
                  fullWidth
                  onClick={addCollaborator}
                  mt="md"
                >
                  <IconPlus size={14} /> Add Collaborator
                </Button>
              </Card>
            </Tabs.Panel>
            {!isShopify && (
              <Tabs.Panel value="paymentMethod">
                <Card withBorder>
                  <AccountPaymentMethod account={account} />
                </Card>
              </Tabs.Panel>
            )}
            <Tabs.Panel value="subscriptions">
              <Card withBorder>
                <AccountSubscriptions />
              </Card>
            </Tabs.Panel>
            <Tabs.Panel value="connectedApps">
              <Card withBorder>
                <SettingsTabConnectedApps accountId={account.id} />
              </Card>
            </Tabs.Panel>
          </Tabs>
        </Grid.Col>
      </Grid>
    </div>
  );
  // TODO: integration active!! button
};
export default SettingsAccountPage;
